import { useEffect, useState } from 'react';
import {
  Card,
  Table,
  TableBody,
  Container,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Grid,
  TextField,
  InputAdornment,
  Box,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Link } from 'react-router-dom';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import Scrollbar from '../../components/Scrollbar';
import { useAppDispatch, useAppSelector } from '../../store';
import { findContactForm, updateContactForm } from '../../store/contactForm/api';
import Loader from '../../components/Loader';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import EditIcon from '@mui/icons-material/Edit';
import {
  reset as resetContactForm,
  setStatus as setContactFormStatus,
} from '../../store/contactForm/contactFormSlice';
import {
  setLimit,
  setSkip,
  setCurrentPage,
} from '../../store/contactForm/contactFormSlice';
import { CONTACT_FORM_TYPE } from '../../constants';
import { encryptString } from '../../utils/url';
import { ContactFormType } from '../../enum';
import { ContactForm } from '../../store/contactForm/api/interface';
import { LoadingButton } from '@mui/lab';
import { updateUserProfile } from '../../store/users/api';
import AutoComplete from '../../components/AutoComplete';
import { fDateLine, fTimeLine } from '../../utils/formatTime';

const Index = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.contactForm);
  const [search, setSearch] = useState<string>('');
  const [type, setType] = useState<string>('');
  const [isReadFilter, setIsReadFilter] = useState<string>('');
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedContactForm, setSelectedContactForm] = useState<ContactForm | null>(null);
  const [commentText, setCommentText] = useState<string>('');
  const [isReadStatus, setIsReadStatus] = useState<boolean>(false);
  const [updatingComment, setUpdatingComment] = useState(false);

  useEffect(() => {
    dispatch(
      findContactForm({
        ...(search && { search: search }),
        limit,
        skip,
        ...(type && { type: type }),
        ...(isReadFilter && { isRead: isReadFilter }),
      })
    );
  }, [dispatch, search, limit, skip, type, isReadFilter]);

  useEffect(() => {
    dispatch(resetContactForm());
  }, [search]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setContactFormStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const [loadingButtonId, setLoadingButtonId] = useState<string | null>(null);

  const editUserDetails = async (contactForm: ContactForm) => {
    setLoadingButtonId(contactForm?.id);
    const data = contactForm?.data;
    if (contactForm?.type === ContactFormType?.changeEmail) {
      const reqBody = {
        id: contactForm?.user?.id,
        name: contactForm?.user?.name,
        email: data?.email ? data?.email : contactForm?.user?.email,
        countryCallingCode: contactForm?.user?.countryCallingCode,
        mobileNumber: contactForm?.user?.mobileNumber,
        activeState: contactForm?.user?.activeState,
        language: contactForm?.user?.language,
        freeAds: contactForm?.user?.freeAds ?? '0',
        maxAds: contactForm?.user?.maxAds ?? '0',
        contactFormId: contactForm?.id,
      };
      await dispatch(updateUserProfile(reqBody)).unwrap();
    } else if (contactForm?.type === ContactFormType?.changeMobileNumber) {
      const reqBody = {
        id: contactForm?.user?.id,
        name: contactForm?.user?.name,
        email: contactForm?.user?.email,
        countryCallingCode: data?.countryCallingCode
          ? data?.countryCallingCode
          : contactForm?.user?.countryCallingCode,
        mobileNumber: data?.mobileNumber
          ? data?.mobileNumber
          : contactForm?.user?.mobileNumber,
        activeState: contactForm?.user?.activeState,
        language: contactForm?.user?.language,
        freeAds: contactForm?.user?.freeAds ?? '0',
        maxAds: contactForm?.user?.maxAds ?? '0',
        contactFormId: contactForm?.id,
      };
      await dispatch(updateUserProfile(reqBody)).unwrap();
    }
    await dispatch(
      findContactForm({
        ...(search && { search: search }),
        limit,
        skip,
        ...(type && { type: type }),
        ...(isReadFilter && { isRead: isReadFilter }),
      })
    );
    setLoadingButtonId(null);
  };

  const handleOpenCommentDialog = (contactForm: ContactForm) => {
    setSelectedContactForm(contactForm);
    setCommentText(contactForm.comment || '');
    setIsReadStatus(contactForm.isRead || false);
    setCommentDialogOpen(true);
  };

  const handleCloseCommentDialog = () => {
    setCommentDialogOpen(false);
    setSelectedContactForm(null);
    setCommentText('');
    setIsReadStatus(false);
  };

  const handleSaveComment = async () => {
    if (!selectedContactForm) return;
    
    setUpdatingComment(true);
    try {
      const updateData: any = {};
      
      // Only allow comment editing for report and inquiry
      if (selectedContactForm.type === ContactFormType.report || 
          selectedContactForm.type === ContactFormType.inquiry) {
        updateData.comment = commentText;
      }
      
      // For changeEmail/changeMobileNumber, isRead is computed from changesApplied
      // For other types, we can update isRead directly
      if (selectedContactForm.type !== ContactFormType.changeEmail && 
          selectedContactForm.type !== ContactFormType.changeMobileNumber) {
        updateData.isRead = isReadStatus;
      }
      
      await dispatch(updateContactForm({ 
        id: selectedContactForm.id, 
        data: updateData 
      })).unwrap();
      
      // Refresh the list
      await dispatch(
        findContactForm({
          ...(search && { search: search }),
          limit,
          skip,
          ...(type && { type: type }),
          ...(isReadFilter && { isRead: isReadFilter }),
        })
      );
      
      handleCloseCommentDialog();
    } catch (error) {
      console.error('Error updating contact form:', error);
    } finally {
      setUpdatingComment(false);
    }
  };

  return (
    <Page title={'Contact Form: List'}>
      <Container maxWidth={'xl'}>
        <Card sx={{ mt: 1.5 }}>
          <Grid
            container
            spacing={2}
            sx={{ py: 2.5, px: 3 }}
          >
            <Grid
              item
              xs={12}
              sm={4}
            >
              <TextField
                fullWidth
                onChange={(e) => setSearch(e.target.value)}
                placeholder={'Search...'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Iconify
                        icon={'eva:search-fill'}
                        sx={{ color: 'text.disabled', width: 20, height: 20 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
            >
              <AutoComplete
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                  } else if (newValue) {
                    setType(newValue.value);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  if (!newInputValue) {
                    setType('');
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props}>{option.name}</li>
                )}
                getOptionLabel={(option) => option.name}
                fetchFunction={() => null}
                label={'Select Type'}
                loading={false}
                options={CONTACT_FORM_TYPE}
                searchFunction={() => null}
                value={
                  CONTACT_FORM_TYPE?.filter(
                    (item) => item.value === type
                  )?.[0] || null
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
            >
              <FormControl fullWidth>
                <InputLabel>Read/Unread</InputLabel>
                <Select
                  value={isReadFilter}
                  label='Read/Unread'
                  onChange={(e) => setIsReadFilter(e.target.value)}
                >
                  <MenuItem value=''>All</MenuItem>
                  <MenuItem value='true'>Read</MenuItem>
                  <MenuItem value='false'>Unread</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Scrollbar>
            <TableContainer sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
              <Table size={'medium'} sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>{'Name'}</TableCell>
                    <TableCell align='center'>{'Mobile No.'}</TableCell>
                    <TableCell align='center'>
                      <div style={{ color: '#64013c' }}>
                        <div style={{ whiteSpace: 'nowrap' }}>{'Date'}</div>
                        <div style={{ whiteSpace: 'nowrap' }}>{'Time'}</div>
                      </div>
                    </TableCell>
                    <TableCell align='center'>{'Type'}</TableCell>
                    <TableCell align='center'>{'Title'}</TableCell>
                    <TableCell align='center'>{'Description'}</TableCell>
                    <TableCell align='center'>{'Comment'}</TableCell>
                    <TableCell align='center'>{'Read/Unread'}</TableCell>
                    <TableCell align='center'>{'AD'}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow hover>
                      <TableCell
                        colSpan={9}
                        align='center'
                      >
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        align='center'
                      >
                        {'No record found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.map((contactForm) => (
                      <TableRow key={contactForm.id}>
                        <TableCell align='center'>
                          {contactForm?.user?.name}
                        </TableCell>
                        <TableCell align='center'>
                          {contactForm?.user?.countryCallingCode}
                          {'-'}
                          {contactForm?.user?.mobileNumber}
                        </TableCell>
                        <TableCell align='center'>
                          {contactForm?.createdAt ? (
                            <Box sx={{ whiteSpace: 'nowrap' }}>
                              <Box component="span" sx={{ display: 'block' }}>
                                {fDateLine(contactForm.createdAt)}
                              </Box>
                              <Box component="span" sx={{ display: 'block' }}>
                                {fTimeLine(contactForm.createdAt)}
                              </Box>
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ textTransform: 'capitalize' }}
                        >
                          {contactForm?.type}
                        </TableCell>
                        <TableCell align='center'>
                          {contactForm?.title}
                        </TableCell>
                        <TableCell align='center'>
                          {contactForm?.type === ContactFormType.changeEmail ? (
                            <Box sx={{ textAlign: 'left' }}>
                              <Box>
                                <strong>Old:</strong>{' '}
                                {contactForm?.data?.oldEmail || contactForm?.user?.email || 'N/A'}
                              </Box>
                              <Box>
                                <strong>New:</strong> {contactForm.data?.email || 'N/A'}
                              </Box>
                              <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.5 }}>
                                <strong>Time:</strong>{' '}
                                {contactForm?.createdAt
                                  ? new Date(contactForm.createdAt).toLocaleString()
                                  : 'N/A'}
                              </Box>
                            </Box>
                          ) : contactForm?.type === ContactFormType.changeMobileNumber ? (
                            <Box sx={{ textAlign: 'left' }}>
                              <Box>
                                <strong>Old:</strong>{' '}
                                {contactForm?.data?.oldCountryCallingCode && contactForm?.data?.oldMobileNumber
                                  ? `${contactForm.data.oldCountryCallingCode}-${contactForm.data.oldMobileNumber}`
                                  : contactForm?.user?.countryCallingCode && contactForm?.user?.mobileNumber
                                  ? `${contactForm.user.countryCallingCode}-${contactForm.user.mobileNumber}`
                                  : 'N/A'}
                              </Box>
                              <Box>
                                <strong>New:</strong>{' '}
                                {contactForm?.data?.countryCallingCode && contactForm?.data?.mobileNumber
                                  ? `${contactForm.data.countryCallingCode}-${contactForm.data.mobileNumber}`
                                  : 'N/A'}
                              </Box>
                              <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.5 }}>
                                <strong>Time:</strong>{' '}
                                {contactForm?.createdAt
                                  ? new Date(contactForm.createdAt).toLocaleString()
                                  : 'N/A'}
                              </Box>
                            </Box>
                          ) : (
                            contactForm?.message
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          {(contactForm?.type === ContactFormType.report || 
                            contactForm?.type === ContactFormType.inquiry) ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <Box sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {contactForm?.comment || '-'}
                              </Box>
                              <IconButton
                                size='small'
                                onClick={() => handleOpenCommentDialog(contactForm)}
                                sx={{ color: 'primary.main' }}
                              >
                                <EditIcon fontSize='small' />
                              </IconButton>
                            </Box>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          <Chip
                            label={contactForm?.isRead ? 'Read' : 'Unread'}
                            color={contactForm?.isRead ? 'success' : 'error'}
                            size='small'
                          />
                        </TableCell>
                        <TableCell align='center'>
                          {contactForm?.advertiseId && (
                            <IconButton aria-label='visibility'>
                              <Link
                                to={`/advertise/view/${encryptString(
                                  contactForm?.advertiseId ?? ''
                                )}`}
                                style={{ color: '#808080' }}
                              >
                                <RemoveRedEyeIcon />
                              </Link>
                            </IconButton>
                          )}
                          {(contactForm?.type === ContactFormType.changeEmail ||
                            contactForm?.type ===
                              ContactFormType.changeMobileNumber) &&
                            !contactForm?.changesApplied && (
                              <LoadingButton
                                variant='contained'
                                loading={loadingButtonId === contactForm.id}
                                onClick={() => editUserDetails(contactForm)}
                              >
                                {'Apply Changes'}
                              </LoadingButton>
                            )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(Math.round(totalRecords / limit), currentPage)}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>

        {/* Comment Edit Dialog */}
        <Dialog
          open={commentDialogOpen}
          onClose={handleCloseCommentDialog}
          maxWidth='md'
          fullWidth
        >
          <DialogTitle>
            {selectedContactForm?.type === ContactFormType.report || 
             selectedContactForm?.type === ContactFormType.inquiry
              ? 'Edit Comment & Status'
              : 'Edit Status'}
          </DialogTitle>
          <DialogContent>
            {(selectedContactForm?.type === ContactFormType.report || 
              selectedContactForm?.type === ContactFormType.inquiry) && (
              <TextField
                fullWidth
                multiline
                rows={4}
                label='Comment'
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                sx={{ mt: 2 }}
                placeholder='Add a comment...'
              />
            )}
            {selectedContactForm?.type !== ContactFormType.changeEmail && 
             selectedContactForm?.type !== ContactFormType.changeMobileNumber && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Read/Unread Status</InputLabel>
                <Select
                  value={isReadStatus ? 'true' : 'false'}
                  label='Read/Unread Status'
                  onChange={(e) => setIsReadStatus(e.target.value === 'true')}
                >
                  <MenuItem value='false'>Unread</MenuItem>
                  <MenuItem value='true'>Read</MenuItem>
                </Select>
              </FormControl>
            )}
            {(selectedContactForm?.type === ContactFormType.changeEmail || 
              selectedContactForm?.type === ContactFormType.changeMobileNumber) && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <strong>Note:</strong> Read/Unread status for {selectedContactForm?.type} is automatically 
                determined by whether changes have been applied. Status will be marked as "Read" when 
                "Apply Changes" is clicked.
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCommentDialog}>Cancel</Button>
            <LoadingButton
              onClick={handleSaveComment}
              loading={updatingComment}
              variant='contained'
            >
              Save
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
};

export default Index;
